import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

type Tone = 'default' | 'inverse'

interface EyebrowProps {
    children: ReactNode
    tone?: Tone
    align?: 'left' | 'center'
    className?: string
}

interface SectionHeadingProps {
    eyebrow?: ReactNode
    title: ReactNode
    description?: ReactNode
    align?: 'left' | 'center'
    tone?: Tone
    headingId?: string
    className?: string
}

export const Eyebrow: FC<EyebrowProps> = ({ children, tone = 'default', align = 'left', className }): ReactNode => {
    return (
        <p
            className={cn(
                'text-xs font-bold tracking-[0.16em] uppercase',
                align === 'center' && 'text-center',
                tone === 'inverse' ? 'text-highlight' : 'text-primary',
                className
            )}
        >
            {children}
        </p>
    )
}

export const SectionHeading: FC<SectionHeadingProps> = ({
    eyebrow,
    title,
    description,
    align = 'center',
    tone = 'default',
    headingId,
    className
}): ReactNode => {
    return (
        <div className={cn('max-w-2xl', align === 'center' && 'mx-auto text-center', className)}>
            {eyebrow && (
                <Eyebrow tone={tone} align={align}>
                    {eyebrow}
                </Eyebrow>
            )}

            <h2
                id={headingId}
                className={cn(
                    'mt-3 text-3xl font-bold tracking-tight sm:text-4xl lg:text-[2.6rem] lg:leading-[1.12]',
                    tone === 'inverse' ? 'text-white' : 'text-foreground'
                )}
            >
                {title}
            </h2>

            {description && (
                <p className={cn('mt-4 text-base leading-relaxed sm:text-lg', tone === 'inverse' ? 'text-ink-muted' : 'text-muted-foreground')}>
                    {description}
                </p>
            )}
        </div>
    )
}
